import { match, P } from 'ts-pattern';
import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItem, ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type {
  IxcFundingV1AchPull,
  IxcFundingV1AchPush,
  IxcFundingV1Approval,
  IxcFundingV1FundingHoldApproval,
  IxcFundingV1IncomingTransfer,
  IxcFundingV1NameAndAddress,
  IxcFundingV1TransferCheck,
  IxcFundingV1TransferCheckType,
  IxcFundingV1TransferData,
  IxcFundingV1TransferKeys,
  IxcFundingV1TransferSpec,
  IxcFundingV1TransferState,
  IxcFundingV1TransferType,
  IxcFundingV1WirePush,
} from '@interxfi/types/generated/api';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import type { ReplaceValuesAndMakeOptional } from '@interxfi/utilities/types/replace';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getPortfolioHoldDetailPath } from '../../portfolio/hold/portfolio-hold-routing';
import { getRegistrationAccountDetailPath } from '../../registration/account/registration-account-routing';
import { getRegistrationAddressListItems } from '../../registration/shared-registration-list-items';
import { getProgramDetailPath } from '../../userauth/program/program-routing';
import { getFundingAccountNumberDetailPath } from '../account-number/funding-account-number-routing';
import { getFundingBatchTransferDetailPath } from '../batch-transfer/funding-batch-transfer-routing';
import { getFundingSourceSpecListItems } from '../source/funding-source-list-items';
import { getFundingSourceDetailPath } from '../source/funding-source-routing';
import { getFundingTransferDetailPath } from './funding-transfer-routing';
import { FundingTransferStatusPill } from './funding-transfer-status-pill';
import { FundingTransferTypePill } from './funding-transfer-type-pill';

export function getFundingNameAndAddressListItems(data: IxcFundingV1NameAndAddress | undefined, prefix = '') {
  return {
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
    address: getRegistrationAddressListItems(data?.address, `${prefix}address`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferWirePushListItems(data: IxcFundingV1WirePush | undefined, prefix = '') {
  return {
    beneficiary: getFundingNameAndAddressListItems(data?.beneficiary, `${prefix}beneficiary`),
    originator: data?.originator ? getFundingNameAndAddressListItems(data.originator, `${prefix}originator`) : undefined,
    messageToRecipient: data?.messageToRecipient
      ? getReadonlyListItemString(`${prefix}messageToRecipient`, 'Message To Recipient', data.messageToRecipient)
      : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferAchPullListItems(data: IxcFundingV1AchPull | undefined, prefix = '') {
  return {
    isSameDayRequested: getReadonlyListItemYesOrNo(`${prefix}isSameDayRequested`, 'Is Same-Day Requested', Boolean(data?.isSameDayRequested)),
    debitAuthorization: getReadonlyListItemYesOrNo(`${prefix}debitAuthorization`, 'Debit Authorization', Boolean(data?.debitAuthorization)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferAchPushListItems(data: IxcFundingV1AchPush | undefined, prefix = '') {
  return {
    isSameDayRequested: getReadonlyListItemYesOrNo(`${prefix}isSameDayRequested`, 'Is Same-Day Requested', Boolean(data?.isSameDayRequested)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferIncomingTransferListItems(data: IxcFundingV1IncomingTransfer | undefined, prefix = '') {
  return {
    accountNumberId: getReadonlyListItemId(
      `${prefix}accountNumberId`,
      'Account Number ID',
      data?.accountNumberId,
      getFundingAccountNumberDetailPath(data?.accountNumberId),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferTypeListItems(data: IxcFundingV1TransferType | undefined, prefix = '') {
  const typeNameColumns = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcFundingV1',
      oneOfName: 'IxcFundingV1TransferType',
      value: getOneOfType(data),
    }),
    typeNamePill: getReadonlyListItemPill(`${prefix}typeNamePill`, 'Type', data ? <FundingTransferTypePill type={data} /> : null),
    typeNamePillWithoutPrefix: getReadonlyListItemPill(
      `${prefix}typeNamePillWithoutPrefix`,
      'Type',
      data ? <FundingTransferTypePill type={data} withoutTypePrefix /> : null,
    ),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumns;
  }

  if ('achPush' in data) {
    return {
      ...typeNameColumns,
      achPush: {
        '!type': 'achPush',
        value: getFundingTransferAchPushListItems(data.achPush, `${prefix}.achPush`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('achPull' in data) {
    return {
      ...typeNameColumns,
      achPull: {
        '!type': 'achPull',
        value: getFundingTransferAchPullListItems(data.achPull, `${prefix}.achPull`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('wirePush' in data) {
    return {
      ...typeNameColumns,
      wirePush: {
        '!type': 'wirePush',
        value: getFundingTransferWirePushListItems(data.wirePush, `${prefix}.wirePush`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('incomingTransfer' in data) {
    return {
      ...typeNameColumns,
      incomingTransfer: {
        '!type': 'incomingTransfer',
        value: getFundingTransferIncomingTransferListItems(data.incomingTransfer, `${prefix}.incomingTransfer`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumns;
}

export function getFundingTransferHoldApprovalListItems(
  data: IxcFundingV1FundingHoldApproval | undefined,
  accountId: string | undefined,
  prefix = '',
) {
  return {
    holdId: getReadonlyListItemId(`${prefix}holdId`, 'Hold ID', data?.holdId, getPortfolioHoldDetailPath(accountId, data?.holdId)),
    outcome: getReadonlyListItemTranslatedString(`${prefix}outcome`, 'Outcome', {
      namespace: 'ixcFundingV1',
      enumName: 'IxcFundingV1CheckOutcome',
      value: data?.outcome,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingApprovalListItems(data: IxcFundingV1Approval | undefined, prefix = '') {
  return {
    outcome: getReadonlyListItemTranslatedString(`${prefix}outcome`, 'Outcome', {
      namespace: 'ixcFundingV1',
      enumName: 'IxcFundingV1CheckOutcome',
      value: data?.outcome,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferCheckTypeListItems(data: IxcFundingV1TransferCheckType | undefined, accountId: string | undefined, prefix = '') {
  const typeNameColumns = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Check Type', {
      namespace: 'ixcFundingV1',
      oneOfName: 'IxcFundingV1TransferCheckType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumns;
  }

  if ('fundingHold' in data) {
    return {
      ...typeNameColumns,
      fundingHold: {
        '!type': 'fundingHold',
        value: {
          hold: getFundingTransferHoldApprovalListItems(data.fundingHold?.hold, accountId, `${prefix}fundingHold.`),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('clearingApproval' in data) {
    return {
      ...typeNameColumns,
      clearingApproval: {
        '!type': 'clearingApproval',
        value: {
          approval: getFundingApprovalListItems(data.clearingApproval.approval, `${prefix}clearingApproval.`),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('programApproval' in data) {
    return {
      ...typeNameColumns,
      programApproval: {
        '!type': 'programApproval',
        value: {
          approval: getFundingApprovalListItems(data.programApproval.approval, `${prefix}programApproval.`),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumns;
}

export function getFundingTransferCheckListItems(data: IxcFundingV1TransferCheck | undefined, accountId: string | undefined, prefix = '') {
  return {
    checkId: getReadonlyListItemId(`${prefix}checkId`, 'Check ID', data?.checkId),
    checkType: getFundingTransferCheckTypeListItems(data?.checkType, accountId, `${prefix}checkType`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferSpecListItems(data: IxcFundingV1TransferSpec | undefined, prefix = '') {
  return {
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    transferType: getFundingTransferTypeListItems(data?.transferType, `${prefix}transferType`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferDataListItems(
  data: IxcFundingV1TransferData | undefined,
  accountId: string | undefined,
  prefix = '',
  sourceLink?: string,
) {
  return {
    spec: getFundingTransferSpecListItems(data?.spec, `${prefix}spec`),
    source: getFundingSourceSpecListItems(data?.source, `${prefix}source`, sourceLink),
    estimatedSettleData: getReadonlyListItemDate(`${prefix}estimatedSettleDate`, 'Estimated Settle Date', data?.estimatedSettleDate, 'date_iso'),
    programBankingName: getReadonlyListItemString(`${prefix}programBankingName`, 'Program Banking Name', data?.programBankingName),
    checks: (data?.checks || []).map((check, i) => getFundingTransferCheckListItems(check, accountId, `${prefix}checks.${i}`)),
    batchId: data?.batchId
      ? getReadonlyListItemId(`${prefix}batchId`, 'Batch ID', data.batchId, getFundingBatchTransferDetailPath(data.batchId))
      : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferStateListItems(
  data: IxcFundingV1TransferState | undefined,
  linkContext: ReplaceValuesAndMakeOptional<IxcFundingV1TransferKeys, string, boolean | string> = {
    accountId: true,
    programId: true,
    sourceId: true,
  },
) {
  const sourceLink =
    typeof linkContext.sourceId === 'string' ? linkContext.sourceId : linkContext.sourceId ? getFundingSourceDetailPath(data?.sourceId) : undefined;

  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    transferId: getReadonlyListItemId(
      'transferId',
      'Transfer ID',
      data?.transferId,
      linkContext.transferId ? getFundingTransferDetailPath(data?.transferId) : undefined,
    ),
    sourceId: getReadonlyListItemId('sourceId', 'Source ID', data?.sourceId, sourceLink),
    accountId: getReadonlyListItemId(
      'accountId',
      'Account ID',
      data?.accountId,
      linkContext.accountId ? getRegistrationAccountDetailPath(data?.accountId) : undefined,
    ),
    programId: getReadonlyListItemId(
      'programId',
      'Program ID',
      data?.programId,
      linkContext.programId ? getProgramDetailPath(data?.programId) : undefined,
    ),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcFundingV1',
      enumName: 'IxcFundingV1TransferStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <FundingTransferStatusPill status={data.status} /> : null),
    data: getFundingTransferDataListItems(data?.data, data?.accountId, 'data', sourceLink),
  } as const satisfies ReadonlyListItemRecord;
}

export function getFundingTransferDetailPageListItems(
  data: IxcFundingV1TransferState | undefined,
  linkContext?: ReplaceValuesAndMakeOptional<IxcFundingV1TransferKeys, string, boolean | string>,
) {
  const items = getFundingTransferStateListItems(data, linkContext);
  const typeDetailHeader = match(items.data.spec.transferType)
    .with(P.union({ achPush: P.not(P.nullish) }, { achPull: P.not(P.nullish) }), () => 'ACH Details')
    .with({ wirePush: P.not(P.nullish) }, () => 'Wire Details')
    .with({ incomingTransfer: P.not(P.nullish) }, () => 'Incoming Transfer Details')
    .otherwise(() => undefined);

  return {
    general: {
      left: [
        { id: 'main', items: [items.data.spec.amount, items.data.source.compactSummary, items.data.estimatedSettleData] },
        ...(typeDetailHeader
          ? [
              {
                id: 'typeDetails',
                title: typeDetailHeader,
                items: [
                  items.data.spec.transferType.typeNamePillWithoutPrefix,
                  ...('achPull' in items.data.spec.transferType
                    ? [items.data.spec.transferType.achPull.value.isSameDayRequested, items.data.spec.transferType.achPull.value.debitAuthorization]
                    : []),
                  ...('achPush' in items.data.spec.transferType ? [items.data.spec.transferType.achPush.value.isSameDayRequested] : []),
                  ...('wirePush' in items.data.spec.transferType
                    ? [
                        { ...items.data.spec.transferType.wirePush.value.beneficiary.name, title: 'Beneficiary Name' },
                        { ...items.data.spec.transferType.wirePush.value.beneficiary.address.formatted, title: 'Beneficiary Address' },
                        ...(items.data.spec.transferType.wirePush.value.originator
                          ? [
                              { ...items.data.spec.transferType.wirePush.value.originator.name, title: 'Originator Name' },
                              { ...items.data.spec.transferType.wirePush.value.originator.address.formatted, title: 'Originator Address' },
                            ]
                          : []),
                        ...(items.data.spec.transferType.wirePush.value.messageToRecipient
                          ? [items.data.spec.transferType.wirePush.value.messageToRecipient]
                          : []),
                      ]
                    : []),
                  ...('incomingTransfer' in items.data.spec.transferType
                    ? [items.data.spec.transferType.incomingTransfer.value.accountNumberId]
                    : []),
                ],
              },
            ]
          : []),
      ] as const satisfies ReadonlyListGroup[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.transferId,
        ...(items.data.batchId ? [items.data.batchId] : []),
        items.sourceId,
        items.accountId,
        items.programId,
      ] as const satisfies ReadonlyListItem[],
    },
  };
}
